import Phaser from "phaser";
import { COLORS, TEXT_STYLES } from "../constants";
import { registerAutomationTarget } from "../automation/automationTags";
import { PrimitiveIcon } from "../avatars/PrimitiveIcon";
import { PrimitiveIconId } from "../avatars/icons/primitiveIcons";

type ButtonConfig = {
  width: number;
  height: number;
  label: string;
  description?: string;
  fillColor?: number;
  strokeColor?: number;
  badge?: {
    text: string;
    fill: number;
  };
  icon?: PrimitiveIconId;
  debugHitArea?: boolean;
  automationName?: string;
  onClick: () => void;
};

export class Button extends Phaser.GameObjects.Container {
  private background: Phaser.GameObjects.Rectangle;
  private strokeColor: number;
  private isEnabled = true;
  private icon?: PrimitiveIcon;

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    {
      width,
      height,
      label,
      description,
      onClick,
      badge,
      fillColor,
      strokeColor,
      icon,
      debugHitArea,
      automationName,
    }: ButtonConfig
  ) {
    super(scene, x, y);

    scene.add.existing(this);
    this.setScrollFactor(0);

    const baseStroke = strokeColor ?? COLORS.buttonStroke;
    this.strokeColor = baseStroke;

    this.background = scene.add.rectangle(
      width / 2,
      height / 2,
      width,
      height,
      fillColor ?? COLORS.buttonFill
    );
    this.background.setOrigin(0.5);
    this.background.setStrokeStyle(2, baseStroke);
    this.background.setScrollFactor(0);

    this.add(this.background);

    if (badge) {
      const badgeText = scene.add
        .text(16, 12, badge.text, {
          ...TEXT_STYLES.body,
          fontSize: "16px",
          color: COLORS.textPrimary,
        })
        .setOrigin(0, 0)
        .setScrollFactor(0);

      const badgeBg = scene.add
        .rectangle(
          badgeText.x - 8,
          badgeText.y - 4,
          badgeText.width + 16,
          badgeText.height + 8,
          badge.fill
        )
        .setOrigin(0, 0)
        .setScrollFactor(0);

      this.add(badgeBg);
      this.add(badgeText);
    }

    // Create icon if provided
    if (icon) {
      const iconSize = 24; // Icon size for buttons
      const iconScale = iconSize / 32; // Icons are designed at 32x32
      this.icon = new PrimitiveIcon(scene, icon, {
        scale: iconScale,
      });

      // Position icon on the left side of the button
      const iconX = 16 + iconSize / 2;
      const iconY = height / 2;
      this.icon.setPosition(iconX, iconY);
      this.add(this.icon.getRoot());
    }

    // Adjust text position based on whether icon is present
    const textX = icon ? width / 2 + 12 : width / 2; // Shift text right if icon present
    const title = scene.add
      .text(textX, description ? height * 0.35 : height / 2, label, TEXT_STYLES.button)
      .setOrigin(0.5)
      .setScrollFactor(0);

    this.add(title);

    if (description) {
      const desc = scene.add
        .text(width / 2, height * 0.7, description, {
          ...TEXT_STYLES.body,
          align: "center",
          wordWrap: { width: width - 24 },
        })
        .setOrigin(0.5)
        .setScrollFactor(0);
      this.add(desc);
    }

    this.setSize(width, height);
    this.setupInteractions(onClick, debugHitArea ?? false, width, height);
    this.registerAutomation(automationName ?? this.toAutomationSlug(label));
  }

  private toAutomationSlug(label: string): string {
    const trimmed = label.trim().toLowerCase();
    const slug = trimmed.replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
    return slug || "button";
  }

  private setupInteractions(
    onClick: () => void,
    debugHitArea: boolean,
    width: number,
    height: number
  ) {
    const hitArea = new Phaser.Geom.Rectangle(0, 0, width, height);

    this.background.setInteractive({
      hitArea,
      hitAreaCallback: Phaser.Geom.Rectangle.Contains,
      useHandCursor: true,
    });

    this.background.on("pointerdown", () => {
      if (!this.isEnabled) return;
      this.pressVisual();
    });

    this.background.on("pointerup", () => {
      if (!this.isEnabled) return;
      onClick();
      this.releaseVisual();
    });

    this.background.on("pointerupoutside", () => {
      if (!this.isEnabled) return;
      this.releaseVisual();
    });

    if (debugHitArea) {
      this.scene.input.enableDebug(this.background);

      this.once(Phaser.GameObjects.Events.DESTROY, () => {
        const input = this.scene.input as Phaser.Input.InputPlugin & {
          disableDebug?: (gameObject: Phaser.GameObjects.GameObject) => void;
        };
        input.disableDebug?.(this.background);
      });
    }

    // If a modal overlay blocks input, ensure it either has disableInteractive() called
    // when hidden or its depth is below the button. InputPlugin.topOnly can also help
    // force the top-most interactive object to win when overlays overlap.
  }

  private pressVisual() {
    // Check if button or scene is destroyed before accessing tweens
    if (!this.scene || !this.scene.tweens || !this.active) {
      return;
    }
    this.scene.tweens.killTweensOf(this);
    this.background.setScale(0.95);
    this.setAlpha(0.92);
  }

  private releaseVisual() {
    // Check if button or scene is destroyed before accessing tweens
    if (!this.scene || !this.scene.tweens || !this.active) {
      return;
    }
    this.scene.tweens.killTweensOf(this);
    this.scene.tweens.add({
      targets: this.background,
      scale: 1,
      duration: 140,
      ease: "Sine.easeOut",
    });
    this.scene.tweens.add({
      targets: this,
      alpha: 1,
      duration: 140,
      ease: "Sine.easeOut",
    });
  }

  private resetVisual() {
    // Check if button or scene is destroyed before accessing tweens
    if (!this.scene || !this.scene.tweens || !this.active) {
      return;
    }
    this.scene.tweens.killTweensOf(this);
    this.background.setScale(1);
    this.setAlpha(this.isEnabled ? 1 : 0.55);
  }

  setEnabled(enabled: boolean) {
    this.isEnabled = enabled;
    if (enabled) {
      this.background.setInteractive({ useHandCursor: true });
    } else {
      this.background.disableInteractive();
    }
    this.background.setStrokeStyle(2, this.strokeColor, enabled ? 1 : 0.35);
    this.setAlpha(enabled ? 1 : 0.55);
    if (this.icon) {
      this.icon.setAlpha(enabled ? 1 : 0.55);
    }
    this.resetVisual();
  }

  private registerAutomation(name: string) {
    const cleanup = registerAutomationTarget(this.scene, {
      type: "button",
      name,
      getBounds: () => this.getBounds(),
      getText: () => name,
    });

    this.once(Phaser.GameObjects.Events.DESTROY, cleanup);
  }
}
